const PREFIX = "[ws]"

function enabled(explicit?: boolean): boolean {
  if (explicit === false) return false
  if (explicit === true) return true
  return typeof window !== "undefined"
}

function timestamp(): string {
  return new Date().toISOString()
}

/** Redact JWT from a websocket URL for safe console output. */
export function redactWsUrl(url: string): string {
  try {
    const u = new URL(url)
    if (u.searchParams.has("token")) {
      u.searchParams.set("token", "***")
    }
    return u.toString()
  } catch {
    return url.replace(/token=[^&]+/, "token=***")
  }
}

const CLOSE_CODE_LABELS: Record<number, string> = {
  1000: "normal closure",
  1001: "endpoint going away",
  1002: "protocol error",
  1003: "unsupported data",
  1005: "no close code received",
  1006: "abnormal closure (connection lost)",
  1007: "invalid payload",
  1008: "policy violation",
  1009: "message too large",
  1010: "extension negotiation failed",
  1011: "server error",
  1012: "service restart",
  1013: "try again later",
  1015: "TLS handshake failure",
  4001: "missing token",
  4003: "invalid token or claims",
}

/** Human-readable close reason from a CloseEvent or code/reason pair. */
export function describeCloseReason(code: number, reason = "", wasClean?: boolean): string {
  const label = CLOSE_CODE_LABELS[code] ?? `close code ${code}`
  const detail = reason.trim()
  const clean =
    wasClean === undefined ? "" : wasClean ? " (clean)" : " (not clean)"
  if (detail) {
    return `${label}${clean}: ${detail}`
  }
  return `${label}${clean}`
}

export function describeReadyState(readyState: number): string {
  switch (readyState) {
    case WebSocket.CONNECTING:
      return "CONNECTING"
    case WebSocket.OPEN:
      return "OPEN"
    case WebSocket.CLOSING:
      return "CLOSING"
    case WebSocket.CLOSED:
      return "CLOSED"
    default:
      return `unknown(${readyState})`
  }
}

type WsLogMessage = {
  ulid?: string
  event: string
  ts_event?: string
  [key: string]: unknown
}

/** Console args for an inbound websocket message (richer detail for telephony events). */
export function formatWsMessageLogArgs(message: WsLogMessage): unknown[] {
  if (message.event === "call_event") {
    return [
      "message",
      "call_event",
      {
        ulid: message.ulid ?? null,
        ts_event: message.ts_event ?? null,
        call_event: message.call_event ?? null,
        call_id: message.call_id ?? null,
        contact_id: message.contact_id ?? null,
        call_mode: message.call_mode ?? null,
        user_id: message.user_id ?? null,
      },
    ]
  }

  return ["message", message.event, message.ulid ?? ""]
}

export function wsLog(enabledFlag: boolean | undefined, ...args: unknown[]): void {
  if (!enabled(enabledFlag)) return
  console.log(PREFIX, timestamp(), ...args)
}

export function wsWarn(enabledFlag: boolean | undefined, ...args: unknown[]): void {
  if (!enabled(enabledFlag)) return
  console.warn(PREFIX, timestamp(), ...args)
}

export function wsDebug(enabledFlag: boolean | undefined, ...args: unknown[]): void {
  if (!enabled(enabledFlag)) return
  console.debug(PREFIX, timestamp(), ...args)
}
